/**
 * Synthesized sound effects for the Bundle Builder box animation.
 *
 * Ported from the marketing builder. Everything is generated with Web Audio — there are no audio
 * files to host or load, which is why this ports into the LMS with no asset work at all.
 *
 * Browsers only permit audio after a user gesture; every trigger here runs from a click, so
 * playback is allowed. The mute preference persists in localStorage.
 */

const MUTE_KEY = 'bb_sound_muted';

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  try {
    if (!ctx) ctx = new Ctor();
    // Safari suspends the context until a gesture; resuming here is safe because every caller is a click.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    return ctx;
  } catch {
    // Browsers cap concurrent AudioContexts, and hardened or embedded webviews can refuse one
    // outright. Sound is decoration — never let it take the box down with it.
    return null;
  }
}

/**
 * Releases the shared context.
 *
 * Matters in the LMS in a way it never did on the marketing page: this lives in a modal the learner
 * can open and close all day, and an audio context left running holds hardware open.
 */
export function releaseAudio(): void {
  if (!ctx) return;
  const dying = ctx;
  ctx = null;
  void dying.close().catch(() => undefined);
}

/**
 * Reduced motion means reduced sensory load, not just fewer transitions.
 *
 * The marketing page gates only its CSS animations on this, so a learner who asked for reduced
 * motion still gets a 1.25s fanfare over a static box. Gating the audio too is the accessible
 * reading of the preference.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Every play function starts here: muted by preference, or quietened by reduced-motion. */
function silenced(): boolean {
  return isMuted() || prefersReducedMotion();
}

export function isMuted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    // Storage blocked (private mode, embedded contexts) — treat as unmuted.
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Storage blocked — sound just won't persist.
  }
}

function noiseBuffer(c: AudioContext, seconds: number): AudioBuffer {
  const buffer = c.createBuffer(1, c.sampleRate * seconds, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/** Filtered-noise sweep: the book sliding across the dividers. */
export function playWhoosh(direction: 'in' | 'out' = 'in'): void {
  if (silenced()) return;
  const c = audioCtx();
  if (!c) return;
  const t = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.3);
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 1.2;
  const [f0, f1] = direction === 'in' ? [500, 2600] : [2200, 400];
  filter.frequency.setValueAtTime(f0, t);
  filter.frequency.exponentialRampToValueAtTime(f1, t + 0.22);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.12, t + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
  src.connect(filter).connect(gain).connect(c.destination);
  src.start(t);
  src.stop(t + 0.3);
}

/** Low, soft landing as the book seats into its slot. */
export function playThunk(): void {
  if (silenced()) return;
  const c = audioCtx();
  if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(120, t);
  osc.frequency.exponentialRampToValueAtTime(60, t + 0.12);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.18, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.16);
}

/**
 * Progressive completion sound: level 1 (Essential) is a light two-note lift, level 2 (Elite) the
 * full arpeggio + shimmer, level 3 (Master) a five-note fanfare with a low root — each tier upgrade
 * should feel bigger than the last.
 */
export function playComplete(level: 1 | 2 | 3 = 2): void {
  if (silenced()) return;
  const c = audioCtx();
  if (!c) return;
  const t = c.currentTime;
  const notes =
    level === 1
      ? [523.25, 783.99] // C5 G5
      : level === 2
      ? [523.25, 659.25, 783.99] // C5 E5 G5
      : [392.0, 523.25, 659.25, 783.99, 1046.5]; // G4 C5 E5 G5 C6
  const step = level === 3 ? 0.11 : 0.09;
  notes.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = c.createGain();
    const start = t + i * step;
    const vol = level === 3 ? 0.16 : 0.14;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(vol, start + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + (level === 3 ? 0.7 : 0.5));
    osc.connect(gain).connect(c.destination);
    osc.start(start);
    osc.stop(start + (level === 3 ? 0.75 : 0.55));
  });
  if (level === 3) {
    const root = c.createOscillator();
    root.type = 'sine';
    root.frequency.value = 130.81; // C3 — the "ultimate" weight underneath
    const rGain = c.createGain();
    rGain.gain.setValueAtTime(0.0001, t);
    rGain.gain.exponentialRampToValueAtTime(0.12, t + 0.06);
    rGain.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    root.connect(rGain).connect(c.destination);
    root.start(t);
    root.stop(t + 1.15);
  }
  if (level >= 2) {
    const shimmer = c.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(1567.98, t + 0.27); // G6
    const sGain = c.createGain();
    sGain.gain.setValueAtTime(0.0001, t + 0.27);
    sGain.gain.exponentialRampToValueAtTime(level === 3 ? 0.07 : 0.05, t + 0.32);
    sGain.gain.exponentialRampToValueAtTime(0.0001, t + (level === 3 ? 1.2 : 0.9));
    shimmer.connect(sGain).connect(c.destination);
    shimmer.start(t + 0.27);
    shimmer.stop(t + (level === 3 ? 1.25 : 0.95));
  }
}
